import React from "react";
import PageIntro from "../../../components/admin/PageIntro";
import SectionCard from "../../../components/admin/SectionCard";
import StatCard from "../../../components/data-display/StatCard";
import InfoCard from "../../../components/admin/InfoCard";
import { adminTheme } from "../../../components/admin/adminTheme";
import { useAuth } from "../../../hooks/useAuth";
import {
  useAuditDashboard,
  useAuditorWorkbench,
} from "../hooks";
import type {
  AuditDashboardResponseDto,
  AuditorWorkbenchResponseDto,
} from "../audit.types";

const AuditDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const staffSubRole = user?.staffSubRole ?? null;

  const isHeadView = staffSubRole === "CEO" || staffSubRole === "AUDIT_HEAD";
  const isAuditorView = staffSubRole === "AUDITOR";

  const headQuery = useAuditDashboard({
    enabled: !isAuditorView,
  });
  const auditorQuery = useAuditorWorkbench({
    enabled: isAuditorView,
  });

  if (isAuditorView) {
    const { data, isLoading, error } = auditorQuery;

    if (isLoading) {
      return (
        <PageIntro
          title="Auditor Workbench"
          message="Loading assigned audits and dispute reviews…"
        />
      );
    }

    if (error || !data) {
      return (
        <PageIntro
          title="Auditor Workbench"
          message="Unable to load audit data. Please try again."
        />
      );
    }

    const workbench: AuditorWorkbenchResponseDto = {
      disputeTasks: {
        disputesToReview: data.disputeTasks?.disputesToReview ?? 0,
        highPriorityDisputes:
          data.disputeTasks?.highPriorityDisputes ?? 0,
        nearingSlaBreaches:
          data.disputeTasks?.nearingSlaBreaches ?? 0,
      },
      auditTasks: {
        cashAuditsAssigned: data.auditTasks?.cashAuditsAssigned ?? 0,
        pricingAuditsAssigned:
          data.auditTasks?.pricingAuditsAssigned ?? 0,
        complianceAuditsAssigned:
          data.auditTasks?.complianceAuditsAssigned ?? 0,
      },
    };

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: adminTheme.space.section,
          padding: adminTheme.space.section,
          background: adminTheme.color.pageBg,
          minHeight: "100%",
        }}
      >
        <PageIntro
          title="Auditor Workbench"
          message="Assigned dispute reviews, audit investigations, and exception follow-up."
        />

        <SectionCard title="Dispute review queue">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: adminTheme.space.lg,
            }}
          >
            <StatCard
              label="Disputes to review"
              value={workbench.disputeTasks.disputesToReview}
            />
            <StatCard
              label="High-priority disputes"
              value={workbench.disputeTasks.highPriorityDisputes}
            />
            <StatCard
              label="SLA-risk items"
              value={workbench.disputeTasks.nearingSlaBreaches}
            />
          </div>
        </SectionCard>

        <SectionCard title="Audit investigations">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: adminTheme.space.lg,
            }}
          >
            <StatCard
              label="Cash audits assigned"
              value={workbench.auditTasks.cashAuditsAssigned}
            />
            <StatCard
              label="Pricing audits assigned"
              value={workbench.auditTasks.pricingAuditsAssigned}
            />
            <StatCard
              label="Compliance audits assigned"
              value={workbench.auditTasks.complianceAuditsAssigned}
            />
          </div>
        </SectionCard>
      </div>
    );
  }

  const { data, isLoading, error } = headQuery;

  if (isLoading) {
    return (
      <PageIntro
        title={isHeadView ? "Audit Head Dashboard" : "Audit Dashboard"}
        message="Loading audit and disputes overview…"
      />
    );
  }

  if (error || !data) {
    return (
      <PageIntro
        title={isHeadView ? "Audit Head Dashboard" : "Audit Dashboard"}
        message="Unable to load audit data. Please try again."
      />
    );
  }

  const dashboard: AuditDashboardResponseDto = {
    kpis: {
      openDisputes: data.kpis?.openDisputes ?? 0,
      highRiskDisputes: data.kpis?.highRiskDisputes ?? 0,
      completedAuditsThisMonth:
        data.kpis?.completedAuditsThisMonth ?? 0,
      pendingAudits: data.kpis?.pendingAudits ?? 0,
    },
    disputes: {
      providerDisputes: data.disputes?.providerDisputes ?? 0,
      customerDisputes: data.disputes?.customerDisputes ?? 0,
      paymentDisputes: data.disputes?.paymentDisputes ?? 0,
    },
    audits: {
      cashAuditsPending: data.audits?.cashAuditsPending ?? 0,
      pricingAuditsPending: data.audits?.pricingAuditsPending ?? 0,
      complianceAuditsPending:
        data.audits?.complianceAuditsPending ?? 0,
    },
  };

  const { kpis, disputes, audits } = dashboard;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: adminTheme.space.section,
        padding: adminTheme.space.section,
        background: adminTheme.color.pageBg,
        minHeight: "100%",
      }}
    >
      <PageIntro
        title={isHeadView ? "Audit Head Dashboard" : "Audit Dashboard"}
        message="Internal audit of processes, disputes pipeline, and SLA adherence."
      />

      <SectionCard title="Audit KPIs">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: adminTheme.space.lg,
          }}
        >
          <StatCard
            label="Open disputes"
            value={kpis.openDisputes}
            helpText={`High risk: ${kpis.highRiskDisputes}`}
          />
          <StatCard
            label="Completed audits"
            value={kpis.completedAuditsThisMonth}
            helpText={`Pending audits: ${kpis.pendingAudits}`}
          />
          <StatCard
            label="High-risk disputes"
            value={kpis.highRiskDisputes}
            helpText="Priority disputes requiring review"
          />
        </div>
      </SectionCard>

      <SectionCard title="Dispute pipeline">
        <InfoCard
          title="Disputes by level"
          body={`Provider disputes: ${disputes.providerDisputes}, Customer disputes: ${disputes.customerDisputes}, Payment disputes: ${disputes.paymentDisputes}.`}
        />
      </SectionCard>

      <SectionCard title="Audit cases">
        <InfoCard
          title="Audit cases by module"
          body={`Cash audits pending: ${audits.cashAuditsPending}, Pricing audits pending: ${audits.pricingAuditsPending}, Compliance audits pending: ${audits.complianceAuditsPending}.`}
        />
      </SectionCard>
    </div>
  );
};

export default AuditDashboardPage;